import type {
  BatchResultByIndex,
  BatchResultByIndexDetailed,
  BatchResultByName,
  BatchResultByNameDetailed,
  CallBatchResult,
  IB24BatchOptions
} from '../../../types/b24'
import type {
  BatchCommandsArrayUniversal,
  BatchCommandsObjectUniversal,
  BatchNamedCommandsUniversal
} from '../../../types/http'
import { AbstractBatch } from '../abstract-batch'
import { ApiVersion } from '../../../types/b24'

export type ActionBatchV3 = {
  calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal | BatchNamedCommandsUniversal
  options?: IB24BatchOptions
}

/**
 * Executes a batch request to the Bitrix24 REST API with a maximum number of commands of no more than 50. `restApi:v3`
 * Allows you to execute multiple requests in a single API call, significantly improving performance.
 *
 * Sends up to 50 commands in a single v3 batch HTTP call and returns their results together.
 * That 50 is the **SDK's** ceiling on v3, not the portal's — 51 commands posted straight at
 * the endpoint answered HTTP 200 with 51 results; see {@link MAX_BATCH_COMMANDS_V3}.
 * Supports array, object, and named-command formats. Unlike `BatchByChunkV3`, it does not split
 * large command sets automatically — callers must keep the command count within the 50-command
 * limit. Compared to `BatchV2`, it routes through the v3 endpoint without a client-side method
 * allowlist.
 */
export class BatchV3 extends AbstractBatch {
  /**
   * Executes a batch request to the Bitrix24 REST API: up to 50 commands in one
   * HTTP call, in array, object or named-command form. `restApi:v3`
   *
   * **The argument reference, the three `calls` formats, the options table and
   * worked examples live on the [batch page](https://bitrix24.github.io/b24jssdk/docs/working-with-the-rest-api/batch-rest-api-ver3/).**
   * Deliberately not repeated here: that copy is compiled on every CI run and
   * this one would not be — no pass type-checks a JSDoc `@example` (#420).
   *
   * What matters while editing this file:
   *   - **50 commands maximum**, and on v3 that number is the SDK's own rather
   *     than the portal's — see {@link MAX_BATCH_COMMANDS_V3} for what was
   *     measured. This action does not split; `BatchByChunkV3` is the one that
   *     does.
   *   - **`getData()` has no `result` envelope here.** It returns the keyed map
   *     or array directly — only `call.make` returns `{ result, time }` (#425).
   *   - **Flags live in `options`.** At the top level they are dropped, which is
   *     why `_warnMisplacedOptions` runs first (#426).
   *   - **v3 batch is all-or-nothing.** Per-command errors do not surface; one
   *     failing command fails the envelope.
   *
   * @template T - The data type returned by batch query commands (default `unknown`)
   * @param {ActionBatchV3} options - `calls` plus an optional `options` bag; see
   *   {@link ActionBatchV3} and {@link IB24BatchOptions} for the members.
   * @returns {Promise<CallBatchResult<T>>} results in the shape of the input:
   *   an array for array input, an object keyed by command name for named input.
   *   With `options.returnAjaxResult` each entry is an `AjaxResult` rather than
   *   the raw payload.
   *
   * @example
   * import type { AjaxResult } from '@bitrix24/b24jssdk'
   *
   * interface Task { id: number, title: string }
   * const response = await b24.actions.v3.batch.make<{ item: Task }>({
   *   calls: {
   *     first: ['tasks.task.get', { taskId: 1 }],
   *     second: ['tasks.task.get', { taskId: 2 }]
   *   },
   *   options: { isHaltOnError: false, returnAjaxResult: true, requestId: 'batch-123' }
   * })
   * if (!response.isSuccess) {
   *   throw new Error(`Problem: ${response.getErrorMessages().join('; ')}`)
   * }
   * const data = response.getData()! as Record<string, AjaxResult<{ item: Task }>>
   * console.log(data['first']!.getData()!.result.item)
   *
   * @warning The maximum number of commands in one batch request is 50.
   * @note A batch request executes faster than sequential single calls,
   *     but if one command fails, the entire batch may fail
   *     (depending on API settings and options).
   */
  /**
   * Overloads, not decoration. The shape of a batch answer is decided by the
   * arguments — a named record of commands answers by name, an array answers by
   * index, and `returnAjaxResult` decides whether each entry is the payload or
   * the `AjaxResult` wrapping it. None of that is visible on the returned value,
   * so without these a caller has the union and no way to narrow it, and has to
   * cast at exactly the point the types are worth having (#518).
   *
   * `T` is **one command's payload** in every overload.
   *
   * The last signature keeps a dynamic `returnAjaxResult` working: when the flag
   * is a `boolean` the compiler cannot read as a literal, the union is still the
   * honest answer.
   */
  public async make<T = unknown>(options: {
    calls: BatchNamedCommandsUniversal
    options: IB24BatchOptions & { returnAjaxResult: true }
  }): Promise<BatchResultByNameDetailed<T>>

  public async make<T = unknown>(options: {
    calls: BatchNamedCommandsUniversal
    options?: IB24BatchOptions & { returnAjaxResult?: false }
  }): Promise<BatchResultByName<T>>

  public async make<T = unknown>(options: {
    calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal
    options: IB24BatchOptions & { returnAjaxResult: true }
  }): Promise<BatchResultByIndexDetailed<T>>

  public async make<T = unknown>(options: {
    calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal
    options?: IB24BatchOptions & { returnAjaxResult?: false }
  }): Promise<BatchResultByIndex<T>>

  public async make<T = unknown>(options: ActionBatchV3): Promise<CallBatchResult<T>>

  public async make<T = unknown>(options: ActionBatchV3): Promise<CallBatchResult<T>> {
    this._warnMisplacedOptions(
      options,
      ['isHaltOnError', 'returnAjaxResult', 'requestId'],
      'options'
    )

    const opts = {
      ...options.options,
      apiVersion: ApiVersion.v3
    }

    // No client-side allowlist: every command is sent to the v3 batch endpoint
    // and the server validates each method (unknown ones come back as errors).
    const response = await this._b24.getHttpClient(ApiVersion.v3).batch<T>(options.calls, opts)

    return this._processBatchResponse<T>(response, options.calls, opts)
  }
}
